import React, { useState } from "react"
import { SeriesRow, GroupRow, SnapshotAsset } from "~/api/snapshot_summaries"
import {
  formatCurrency,
  formatCurrencyFull,
  formatPercent,
  formatTvpi,
  formatIrr,
  formatAge,
} from "./utils"
import type { Grouping, View } from "./utils"
import Tooltip from "~/components/Tooltip"

interface SummaryRowProps {
  row: SeriesRow | GroupRow
  grouping: Grouping
  view: View
}

function isSeriesRow(row: SeriesRow | GroupRow): row is SeriesRow {
  return "series" in row
}

function valueClass(value: number | null | undefined): string {
  if (value == null) return ""
  if (value > 0) return "b-cell--positive"
  if (value < 0) return "b-cell--negative"
  return ""
}

function tvpiClass(tvpi: number | null | undefined): string {
  if (tvpi == null) return ""
  return tvpi >= 1.0 ? "b-cell--positive" : "b-cell--negative"
}

// Renders a currency td with a tooltip showing the full unabbreviated value
function cTd(value: number | null | undefined, extraClass?: string) {
  return (
    <td className={extraClass}>
      <Tooltip tip={value != null ? formatCurrencyFull(value) : undefined}>
        {formatCurrency(value)}
      </Tooltip>
    </td>
  )
}

function vTd(value: number | null | undefined) {
  return cTd(value, valueClass(value))
}

// Asset sub-rows mirror the series column structure (performance view only).
// Columns mapped: label→name, sector→sector, called$→cost, dist→dist QTD,
// mv→fair_value, tvpi→tvpi, pl→pl. Unrelated columns are left empty.
// TODO: When SnapshotHolding exists, asset data can be sourced from there
// rather than derived from the snapshot JSONB at cache-build time.
function AssetRow({ asset }: { asset: SnapshotAsset }) {
  const rowClass = asset.is_other
    ? "b-snapshot-summary-asset-row b-snapshot-summary-asset-row--other"
    : "b-snapshot-summary-asset-row"
  return (
    <tr className={rowClass}>
      <td className="b-snapshot-summary-asset-label-cell">{asset.name}</td>
      <td />
      <td />
      <td className="b-snapshot-summary-asset-sector">{asset.sector || "—"}</td>
      <td />
      <td />
      {cTd(asset.cost != null ? -asset.cost : null)}
      {cTd(asset.dist_itd)}
      {cTd(asset.fair_value)}
      {vTd(asset.pl)}
      <td className={tvpiClass(asset.tvpi)}>{formatTvpi(asset.tvpi)}</td>
      <td />
      <td />
      <td />
      <td />
      <td />
      <td />
      <td />
      <td />
      <td />
    </tr>
  )
}

const SummaryRow = ({ row, grouping, view }: SummaryRowProps) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const label = isSeriesRow(row) ? row.series : row.label
  const isSeries = grouping === "series"

  const rowClass = isSeries
    ? "b-snapshot-summary-row"
    : "b-snapshot-summary-row b-snapshot-summary-row--group"

  const p = row.performance
  const qa = row.quarter_activity
  const y = row.ytd_performance

  const labelLink =
    isSeriesRow(row) && row.net_snapshot_url ? (
      <a href={row.net_snapshot_url} className="b-link">
        {label}
      </a>
    ) : (
      label
    )

  const assets = isSeriesRow(row) ? row.assets ?? [] : []
  const hasAssets = isSeries && view === "performance" && assets.length > 0

  return (
    <>
      <tr className={rowClass}>
        <td className="b-snapshot-summary-label-cell">
          {hasAssets && (
            <button
              className={`b-snapshot-summary-expand-btn${
                isExpanded ? " expanded" : ""
              }`}
              onClick={() => setIsExpanded((v) => !v)}
              aria-label={isExpanded ? "Collapse assets" : "Expand assets"}
            >
              {isExpanded ? "▼" : "▶"}
            </button>
          )}
          {labelLink}
        </td>
        {isSeries && <td>{isSeriesRow(row) ? row.vintage : null}</td>}
        {isSeries && <td>{isSeriesRow(row) ? row.asset_class_label : null}</td>}
        {isSeries && <td>{isSeriesRow(row) ? row.sector || "—" : null}</td>}
        {!isSeries && <td>{!isSeriesRow(row) ? row.series_count : null}</td>}
        {cTd(row.commitment)}
        {view === "performance" && (
          <>
            <td>{formatPercent(p.called_pct)}</td>
            {cTd(p.called != null ? -p.called : null)}
            {cTd(p.dist)}
            {cTd(p.mv)}
            {vTd(p.pl)}
            <td className={tvpiClass(p.tvpi)}>{formatTvpi(p.tvpi)}</td>
            {isSeries && <td>{formatIrr(p.irr)}</td>}
            <td className={tvpiClass(p.dpi)}>{formatTvpi(p.dpi)}</td>
            {isSeries && <td>{formatAge(p.age)}</td>}
            {vTd(qa.pl)}
            {vTd(qa.net_cf)}
            <td className={valueClass(qa.irr_change ?? null)}>
              {qa.irr_change != null ? formatIrr(qa.irr_change) : "—"}
            </td>
            <td>{formatPercent(p.mv_pct)}</td>
            <td>{formatPercent(p.pl_pct)}</td>
            <td>{formatPercent(qa.qoq_pl_pct)}</td>
          </>
        )}
        {view === "quarter_activity" && (
          <>
            {vTd(qa.net_cf)}
            {vTd(qa.pl)}
            {vTd(qa.delta_mv)}
            <td className={valueClass(qa.delta_mv_pct)}>
              {formatPercent(qa.delta_mv_pct)}
            </td>
            <td>{formatPercent(qa.called_pct)}</td>
            {cTd(qa.called != null ? -qa.called : null)}
            {cTd(qa.dist)}
          </>
        )}
        {view === "ytd" && (
          <>
            <td>{formatPercent(y.called_pct)}</td>
            {cTd(y.called != null ? -y.called : null)}
            {cTd(y.dist)}
            {vTd(y.delta_mv)}
            {vTd(y.net_cf)}
            {vTd(y.pl)}
          </>
        )}
      </tr>
      {hasAssets &&
        isExpanded &&
        assets.map((asset) => (
          <AssetRow key={asset.portfolio_company_id ?? "other"} asset={asset} />
        ))}
    </>
  )
}

export default SummaryRow
